import type { StreamStatusEntry, StreamStatusEntryIcon } from '../types/chat';

const KAOMOJI = {
  default: [
    'ʕ•ᴥ•ʔ',
    '٩(◕‿◕｡)۶',
    '(ง ͠° ͟ل͜ ͡°)ง',
    '(☉_☉)',
    'ヽ(´ー｀)ノ',
    '(￣▽￣)',
  ],
  happy: [
    '٩(◕‿◕｡)۶',
    '(☞ﾟヮﾟ)☞',
    'ʕ•ᴥ•ʔ',
    '(＾▽＾)',
    'ヽ(・∀・)ﾉ',
    '(ﾉ◕ヮ◕)ﾉ*:･ﾟ✧',
    '♪(´ε｀ )',
  ],
  thinking: [
    '(☉_☉)',
    'ヽ༼ຈل͜ຈ༽ﾉ',
    'ʕ•ᴥ•ʔ',
    '(・_・;)',
    '(￣ヘ￣)',
    '┐(´д`)┌',
    '(；￣Д￣)',
  ],
  working: [
    '(ง ͠° ͟ل͜ ͡°)ง',
    'ლ(ಠ益ಠ)ლ',
    '(ノ°▽°)ノ',
    'ᕙ(⇀‸↼‶)ᕗ',
    '(ง•̀_•́)ง',
    'ᕦ(ò_óˇ)ᕤ',
  ],
  reading: [
    '( ͡~ ͜ʖ ͡°)',
    'ʕ•ᴥ•ʔ',
    '(☉_☉)',
    '(⌐■_■)',
    '(•̀ᴗ•́)و',
    'ヾ(´〇`)ﾉ',
    '(づ｡◕‿‿◕｡)づ',
  ],
  success: [
    '٩(◕‿◕｡)۶',
    '(☞ﾟヮﾟ)☞',
    'ヽ(´▽`)/',
    '(ﾉ´ヮ`)ﾉ*: ･ﾟ',
    '＼(^o^)／',
    '(๑˃ᴗ˂)ﻭ',
  ],
  error: [
    '(ಥ﹏ಥ)',
    '(ノಠ益ಠ)ノ彡┻━┻',
    '(╯°□°)╯︵ ┻━┻',
    '(；′⌒`)',
    '(T_T)',
  ],
  skipped: [
    String.raw`¯\_(ツ)_/¯`,
    '┐(´ー｀)┌',
    '╮(￣▽￣)╭',
  ],
} as const;

const ICON_KAOMOJI: Record<StreamStatusEntryIcon, readonly string[]> = {
  skill_used: KAOMOJI.thinking,
  skill_resource: KAOMOJI.reading,
  skill_skipped: KAOMOJI.skipped,
  tool_started: KAOMOJI.working,
  tool_completed: KAOMOJI.success,
  subagent_started: KAOMOJI.working,
  subagent_completed: KAOMOJI.success,
  processing_started: KAOMOJI.happy,
};

function pickFromList(items: readonly string[], seed: string): string {
  if (!items.length) return KAOMOJI.default[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash + (seed.codePointAt(i) ?? 0) * (i + 1)) % items.length;
  }
  return items[Math.abs(hash) % items.length];
}

function pickByLabel(label: string, seed: string): string | null {
  const normalized = label.toLowerCase();

  if (/search|retriev|look|scan|find|source/.test(normalized)) {
    return pickFromList(KAOMOJI.reading, seed);
  }
  if (/complete|found|success|done|ready/.test(normalized)) {
    return pickFromList(KAOMOJI.success, seed);
  }
  if (/error|fail|unable|sorry/.test(normalized)) {
    return pickFromList(KAOMOJI.error, seed);
  }
  if (/think|analy|plan|understand/.test(normalized)) {
    return pickFromList(KAOMOJI.thinking, seed);
  }

  return null;
}

/** Pick a kaomoji that matches a rotating working-status label. */
export function pickKaomojiForWorkingStatusLabel(label: string): string {
  const fromLabel = pickByLabel(label, label);
  if (fromLabel) return fromLabel;

  const normalized = label.toLowerCase();
  if (/working|on it|moment|process|taking|handling|compact/.test(normalized)) {
    return pickFromList(KAOMOJI.working, label);
  }

  return pickFromList(KAOMOJI.default, label);
}

/** Pick one contextual kaomoji for the assistant "Working" state. */
export function pickWorkingKaomoji(entries: StreamStatusEntry[] = []): string {
  const lastEntry = entries[entries.length - 1];
  if (!lastEntry) {
    return pickFromList(KAOMOJI.default, 'working');
  }

  const fromLabel = pickByLabel(lastEntry.label, lastEntry.id);
  if (fromLabel) return fromLabel;

  const icon: StreamStatusEntryIcon = lastEntry.icon;
  return pickFromList(ICON_KAOMOJI[icon] ?? KAOMOJI.default, lastEntry.id);
}
